import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Input, Label, Table } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import CompanyContext from '../../auth/CompanyContext';
import ConfirmButton from '../Modal/ConfirmButton';
import * as utilities from '../../utilities';

const ContactsNDA2 = ({
    clientToken,
    contactGUID,
    user,
    contactSnapshot = null,
    isActive = false
}) => {
    const companyCtx = useContext(CompanyContext);
    const mountedRef = useRef(true);

    const [ndaRequests, setNdaRequests] = useState([]);
    const [ndaRequestsLoading, setNdaRequestsLoading] = useState(false);
    const [ndaSending, setNdaSending] = useState(false);
    const [ndaRefreshingIDs, setNdaRefreshingIDs] = useState([]);
    const [ndaOpeningDocIDs, setNdaOpeningDocIDs] = useState([]);
    const [ndaDeletingIDs, setNdaDeletingIDs] = useState([]);
    const [ndaRemindingIDs, setNdaRemindingIDs] = useState([]);
    const [ndaRetryingIDs, setNdaRetryingIDs] = useState([]);
    const [ndaCountersigners, setNdaCountersigners] = useState([]);
    const [ndaCountersignersLoading, setNdaCountersignersLoading] = useState(false);
    const [selectedNdaCountersignerAuth0, setSelectedNdaCountersignerAuth0] = useState('');
    const [delegatedSignatureConfig, setDelegatedSignatureConfig] = useState(null);
    const [delegatedSignatureConfigLoading, setDelegatedSignatureConfigLoading] = useState(false);
    const [delegatedSignatureConfigError, setDelegatedSignatureConfigError] = useState('');
    const [automaticCountersign, setAutomaticCountersign] = useState(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const formatDateTime = useCallback((value) => {
        if (!value) return '';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return date.toLocaleString(undefined, {
            timeZone: userTimeZone,
            timeZoneName: 'short'
        });
    }, []);

    const getNdaRequests = useCallback((silent = false) => {
        if (!contactGUID) {
            setNdaRequests([]);
            setNdaRequestsLoading(false);
            return Promise.resolve();
        }

        if (!silent) setNdaRequestsLoading(true);

        return fetch(`/api/pdcflow/contacts/${contactGUID}/requests`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;
                if (!results.success) throw new Error(results.error || 'Unable to load NDA requests');
                setNdaRequests(Array.isArray(results.data) ? results.data : []);
            })
            .catch(() => {
                if (!mountedRef.current) return;
                utilities.addToast('Unable to load NDA requests.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                if (!silent) setNdaRequestsLoading(false);
            });
    }, [contactGUID, clientToken, companyCtx.company]);

    const getNdaCountersigners = useCallback(() => {
        setNdaCountersignersLoading(true);
        return fetch('/api/users/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!mountedRef.current) return;
                const activeUsers = (Array.isArray(results?.data) ? results.data : [])
                    .filter(item => (item?.Active === true || item?.Active === 1) && String(item?.['Email Address'] || '').trim() !== '');
                setNdaCountersigners(activeUsers);

                if (activeUsers.length === 0) {
                    setSelectedNdaCountersignerAuth0('');
                    return;
                }

                setSelectedNdaCountersignerAuth0((prev) => {
                    if (prev && activeUsers.some(item => String(item?.Auth0 || '') === String(prev))) return prev;
                    const currentAuth0 = String(user || '');
                    const currentUser = activeUsers.find(item => String(item?.Auth0 || '') === currentAuth0);
                    if (currentUser?.Auth0) return String(currentUser.Auth0);
                    return String(activeUsers[0]?.Auth0 || '');
                });
            })
            .catch(() => {
                if (!mountedRef.current) return;
                setNdaCountersigners([]);
                setSelectedNdaCountersignerAuth0('');
                utilities.addToast('Unable to load countersigners.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaCountersignersLoading(false);
            });
    }, [clientToken, companyCtx.company, user]);

    const getDelegatedSignatureConfiguration = useCallback(() => {
        setDelegatedSignatureConfigLoading(true);
        setDelegatedSignatureConfigError('');
        return fetch('/api/pdcflow/delegated-signature', {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(response => response.json())
            .then(results => {
                if (!mountedRef.current) return;
                if (!results.success) throw new Error(results.error || 'Unable to load delegated signature configuration');
                setDelegatedSignatureConfig(results.data || null);
                setAutomaticCountersign(false);
            })
            .catch((error) => {
                if (!mountedRef.current) return;
                setDelegatedSignatureConfig(null);
                setDelegatedSignatureConfigError(error?.message || 'Unable to load delegated signature configuration');
                setAutomaticCountersign(false);
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setDelegatedSignatureConfigLoading(false);
            });
    }, [clientToken, companyCtx.company]);

    const sendNda = useCallback(() => {
        if (!contactGUID || ndaSending) return;
        if (!automaticCountersign) {
            utilities.addToast('Confirm the delegated signature before sending the NDA.', 5, 'warning');
            return;
        }

        const selectedCountersigner = ndaCountersigners.find(item => String(item?.Auth0 || '') === String(selectedNdaCountersignerAuth0 || '')) || null;
        if (!automaticCountersign && ndaCountersigners.length > 0 && !selectedCountersigner) {
            utilities.addToast('Select a countersigner before sending.', 5, 'warning');
            return;
        }

        setNdaSending(true);
        fetch('/api/pdcflow/nda/send', {
            method: 'POST',
            body: JSON.stringify({
                contactGUID,
                channel: 'email',
                automaticCountersign,
                contactOverrides: contactSnapshot ? {
                    contactName: String(contactSnapshot.contactName || ''),
                    title: String(contactSnapshot.title || ''),
                    email: String(contactSnapshot.email || ''),
                    phone: String(contactSnapshot.phone || ''),
                    companyName: String(contactSnapshot.companyName || '')
                } : null,
                countersigner: !automaticCountersign && selectedCountersigner ? {
                    auth0: selectedCountersigner.Auth0,
                    firstName: selectedCountersigner['First Name'],
                    lastName: selectedCountersigner['Last Name'],
                    email: selectedCountersigner['Email Address'],
                    phone: selectedCountersigner['Phone Number'] || '',
                    title: selectedCountersigner.Title || ''
                } : null
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!results.success) throw new Error(results.error || 'Unable to send NDA');
                utilities.addToast('NDA sent.', 3, 'success');
                setAutomaticCountersign(false);
                return getNdaRequests(true);
            })
            .catch((err) => {
                utilities.addToast(err?.message || 'Unable to send NDA.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaSending(false);
            });
    }, [contactGUID, ndaSending, clientToken, companyCtx.company, getNdaRequests, ndaCountersigners, selectedNdaCountersignerAuth0, contactSnapshot, automaticCountersign]);

    const refreshNdaList = useCallback(() => {
        if (!contactGUID) return;

        const signatures = ndaRequests
            .map(request => String(request?.signatureID || '').trim())
            .filter(Boolean);

        setNdaRequestsLoading(true);
        setNdaRefreshingIDs(signatures);

        const refreshPromises = signatures.map(signatureID => {
            return fetch(`/api/pdcflow/requests/${encodeURIComponent(signatureID)}/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + clientToken,
                    'X-Database': companyCtx.company
                }
            })
                .then(r => r.json())
                .then(results => {
                    if (!results.success) throw new Error(results.error || 'Unable to refresh status');
                    return true;
                });
        });

        Promise.allSettled(refreshPromises)
            .then((results) => {
                const failed = results.filter(item => item.status === 'rejected').length;
                if (failed > 0) {
                    utilities.addToast(`Refreshed with ${failed} failure${failed === 1 ? '' : 's'}.`, 5, 'warning');
                }
                return getNdaRequests(true);
            })
            .catch(() => {
                utilities.addToast('Unable to refresh NDA list.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaRefreshingIDs([]);
                setNdaRequestsLoading(false);
            });
    }, [contactGUID, ndaRequests, clientToken, companyCtx.company, getNdaRequests]);

    const deleteNdaRequest = useCallback((signatureID) => {
        const id = String(signatureID || '').trim();
        if (!id) return;

        setNdaDeletingIDs(prev => (prev.includes(id) ? prev : [...prev, id]));

        fetch(`/api/pdcflow/requests/${encodeURIComponent(id)}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!results.success) throw new Error(results.error || 'Unable to delete NDA request');
                setNdaRequests(prev => prev.filter(item => String(item?.signatureID || '') !== id));
                utilities.addToast('NDA request deleted.', 3, 'success');
            })
            .catch((err) => {
                utilities.addToast(err?.message || 'Unable to delete NDA request.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaDeletingIDs(prev => prev.filter(item => item !== id));
            });
    }, [clientToken, companyCtx.company]);

    const remindCountersigner = useCallback((request) => {
        const signatureID = String(request?.signatureID || '').trim();
        if (!signatureID) return;

        setNdaRemindingIDs(prev => (prev.includes(signatureID) ? prev : [...prev, signatureID]));

        fetch(`/api/pdcflow/requests/${encodeURIComponent(signatureID)}/remind-countersigner`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(r => r.json())
            .then(results => {
                if (!results.success) throw new Error(results.error || 'Unable to resend countersignature request');
                utilities.addToast('Countersignature request resent.', 3, 'success');
                return getNdaRequests(true);
            })
            .catch((err) => {
                utilities.addToast(err?.message || 'Unable to resend countersignature request.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaRemindingIDs(prev => prev.filter(item => item !== signatureID));
            });
    }, [clientToken, companyCtx.company, getNdaRequests]);

    const retryAutomaticCountersign = useCallback((request) => {
        const signatureID = String(request?.signatureID || '').trim();
        if (!signatureID) return;
        setNdaRetryingIDs(prev => prev.includes(signatureID) ? prev : [...prev, signatureID]);
        fetch(`/api/pdcflow/requests/${encodeURIComponent(signatureID)}/retry-automatic-countersign`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(response => response.json())
            .then(results => {
                if (!results.success) throw new Error(results.error || 'Unable to retry automatic countersignature');
                utilities.addToast('Automatic countersignature completed.', 3, 'success');
                return getNdaRequests(true);
            })
            .catch(error => utilities.addToast(error?.message || 'Unable to retry automatic countersignature.', 7, 'danger'))
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaRetryingIDs(prev => prev.filter(item => item !== signatureID));
            });
    }, [clientToken, companyCtx.company, getNdaRequests]);

    const openSignedNda = useCallback((request) => {
        const signatureID = String(request?.signatureID || '').trim();
        if (!signatureID) return;

        setNdaOpeningDocIDs(prev => (prev.includes(signatureID) ? prev : [...prev, signatureID]));

        const fallbackUrl = `/api/pdcflow/requests/${encodeURIComponent(signatureID)}/signed-document${request?.documentID ? `?documentId=${encodeURIComponent(request.documentID)}` : ''}`;
        const requestUrl = request?.signedDocumentUrl || fallbackUrl;

        fetch(requestUrl, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + clientToken,
                'X-Database': companyCtx.company
            }
        })
            .then(async (response) => {
                if (!response.ok) {
                    let message = 'Unable to load signed NDA.';
                    try {
                        const payload = await response.json();
                        message = payload?.error || message;
                    } catch (err) {
                        const text = await response.text();
                        if (text) message = text;
                    }
                    throw new Error(message);
                }
                return response.blob();
            })
            .then((blob) => {
                const objectUrl = window.URL.createObjectURL(blob);
                const popup = window.open(objectUrl, '_blank', 'noopener,noreferrer');
                if (!popup) {
                    window.URL.revokeObjectURL(objectUrl);
                    throw new Error('Popup blocked. Allow popups to view the signed NDA.');
                }
                setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
            })
            .catch((err) => {
                utilities.addToast(err?.message || 'Unable to open signed NDA.', 5, 'danger');
            })
            .finally(() => {
                if (!mountedRef.current) return;
                setNdaOpeningDocIDs(prev => prev.filter(item => item !== signatureID));
            });
    }, [clientToken, companyCtx.company]);

    useEffect(() => {
        if (!isActive) return;
        getNdaCountersigners();
        getDelegatedSignatureConfiguration();
    }, [isActive, getNdaCountersigners, getDelegatedSignatureConfiguration]);

    useEffect(() => {
        if (!isActive) return;

        if (!contactGUID) {
            setNdaRequests([]);
            setNdaRequestsLoading(false);
            setNdaRefreshingIDs([]);
            setNdaOpeningDocIDs([]);
            setNdaDeletingIDs([]);
            setNdaRemindingIDs([]);
            setNdaRetryingIDs([]);
            return;
        }

        setNdaRefreshingIDs([]);
        setNdaOpeningDocIDs([]);
        setNdaDeletingIDs([]);
        setNdaRemindingIDs([]);
        setNdaRetryingIDs([]);
        getNdaRequests();
    }, [isActive, contactGUID, getNdaRequests]);

    const canUseAutomaticCountersign = delegatedSignatureConfig?.enabled === true
        && delegatedSignatureConfig?.authorized === true
        && delegatedSignatureConfig?.configured === true;
    const showAutomaticCountersign = delegatedSignatureConfigLoading
        || delegatedSignatureConfigError
        || delegatedSignatureConfig?.authorized === true;
    const automaticCountersignUnavailableReason = delegatedSignatureConfigError
        || (delegatedSignatureConfig?.enabled === false
            ? 'Automatic signing is disabled for this environment.'
            : delegatedSignatureConfig?.authorized === true && delegatedSignatureConfig?.configured === false
                ? 'Unavailable until a document signature is uploaded for one active user in Admin > Users.'
                : '');
    const canSendNda = !!contactGUID
        && !ndaSending
        && !ndaCountersignersLoading
        && canUseAutomaticCountersign
        && automaticCountersign;

    if (!isActive) return null;

    return (
        <>
            <div className='d-flex justify-content-between align-items-start mb-4'>
                <Button
                    color="secondary"
                    className='align-self-start'
                    onClick={refreshNdaList}
                    disabled={ndaRequestsLoading || !contactGUID}
                >
                    {ndaRequestsLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <div className='d-flex align-items-start flex-wrap justify-content-end'>
                    {showAutomaticCountersign ? (
                        <div className='mr-3 mb-2 text-left' style={{ maxWidth: 530 }}>
                            <Label check className='m-0'>
                                <Input
                                    type="checkbox"
                                    checked={automaticCountersign}
                                    onChange={event => setAutomaticCountersign(event.target.checked)}
                                    disabled={ndaSending || !canUseAutomaticCountersign}
                                />
                                {delegatedSignatureConfigLoading
                                    ? ' Loading automatic signature configuration...'
                                    : ` Automatically apply ${delegatedSignatureConfig?.signer?.name || 'the configured signer'}'s delegated signature after the recipient signs`}
                                <small className={`${automaticCountersignUnavailableReason ? 'text-danger' : 'text-muted'} d-block mt-1`}>
                                    {automaticCountersignUnavailableReason || delegatedSignatureConfig?.authorizationText}
                                </small>
                            </Label>
                            {/* {automaticCountersignUnavailableReason || automaticCountersign ? ( */}
                            {/* ) : null} */}
                        </div>
                    ) : null}
                    {/* Temporarily hidden while this iteration requires delegated automatic countersigning.
                    <div className='mr-2'>
                        <Label className='m-0 d-block'>
                            <Input
                                type="select"
                                value={selectedNdaCountersignerAuth0}
                                onChange={(e) => setSelectedNdaCountersignerAuth0(String(e.target.value || ''))}
                                disabled={automaticCountersign || ndaCountersignersLoading || ndaSending || ndaCountersigners.length === 0}
                                style={{ minWidth: 260 }}
                            >
                                {ndaCountersignersLoading ? (
                                    <option value=''>Loading users...</option>
                                ) : null}
                                {!ndaCountersignersLoading && ndaCountersigners.length === 0 ? (
                                    <option value=''>No active users found</option>
                                ) : null}
                                {!ndaCountersignersLoading && ndaCountersigners.map(userOption => (
                                    <option key={String(userOption.Auth0 || '')} value={String(userOption.Auth0 || '')}>
                                        {String(userOption['Full Name'] || `${userOption['First Name'] || ''} ${userOption['Last Name'] || ''}`.trim() || userOption['Email Address'] || 'User')}
                                    </option>
                                ))}
                            </Input>
                            Countersigner</Label>
                    </div>
                    */}
                    <Button
                        color="success"
                        className='align-self-start'
                        onClick={sendNda}
                        disabled={!canSendNda}
                    >
                        {ndaSending ? 'Sending...' : 'Send NDA'}
                    </Button>
                </div>
            </div>

            {ndaRequests.length === 0 ? (
                <div className='text-muted small'>No NDA requests yet.</div>
            ) : (
                <Table className="text-left">
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th>Status Date</th>
                            <th>Request ID</th>
                            <th className='text-right'>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ndaRequests.map(request => {
                            const isRefreshing = ndaRefreshingIDs.includes(request.signatureID);
                            const isOpeningDoc = ndaOpeningDocIDs.includes(request.signatureID);
                            const isDeleting = ndaDeletingIDs.includes(request.signatureID);
                            const isReminding = ndaRemindingIDs.includes(request.signatureID);
                            const isRetrying = ndaRetryingIDs.includes(request.signatureID);
                            const statusLabel = String(request.statusLabel || request.status || '').toLowerCase();
                            const automatic = request?.automaticCountersign || null;
                            const isSigned = automatic
                                ? String(automatic.status || '').toUpperCase() === 'COMPLETED'
                                : (statusLabel === 'signed' || String(request.status || '').toUpperCase() === 'COMPLETED');
                            const countersignature = request?.countersignature || null;
                            const isAwaitingCountersignature = countersignature?.awaiting === true;
                            return (
                                <tr key={request.signatureID}>
                                    <td>
                                        <strong>{String(request.statusLabel || request.status || 'sent').toUpperCase()}</strong>
                                        {automatic?.lastError ? <small className='text-danger d-block'>{automatic.lastError}</small> : null}
                                    </td>
                                    <td className='text-muted'>{formatDateTime(automatic?.completedDate || request.statusDate || request.updatedDate || request.requestedDate)}</td>
                                    <td className='text-muted'>{request.signatureID}</td>
                                    <td className='text-right'>
                                    {isSigned ? (
                                        <Button
                                            color="primary"
                                            className='mt-1 mr-2'
                                            onClick={() => openSignedNda(request)}
                                            disabled={isOpeningDoc || isDeleting || isRefreshing || !isSigned}
                                        >
                                            {isOpeningDoc ? 'Opening...' : 'View Signed PDF'}
                                        </Button>
                                    ) : null}
                                    {isAwaitingCountersignature && countersignature?.canContinue && countersignature?.continueUrl ? (
                                        <Button
                                            tag="a"
                                            color="primary"
                                            className='mt-1 mr-2'
                                            href={countersignature.continueUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            disabled={isDeleting || isRefreshing || isReminding}
                                        >
                                            Continue Countersigning
                                        </Button>
                                    ) : null}
                                    {isAwaitingCountersignature && !countersignature?.canContinue && countersignature?.canRemind ? (
                                        <Button
                                            color="secondary"
                                            className='mt-1 mr-2'
                                            onClick={() => remindCountersigner(request)}
                                            disabled={isDeleting || isRefreshing || isReminding}
                                        >
                                            {isReminding ? 'Resending...' : 'Resend to Countersigner'}
                                        </Button>
                                    ) : null}
                                    {automatic?.canRetry && delegatedSignatureConfig?.authorized === true ? (
                                        <Button
                                            color="warning"
                                            className='mt-1 mr-2'
                                            onClick={() => retryAutomaticCountersign(request)}
                                            disabled={isDeleting || isRefreshing || isRetrying}
                                        >
                                            {isRetrying ? 'Retrying...' : 'Retry Automatic Signature'}
                                        </Button>
                                    ) : null}
                                    <ConfirmButton
                                        color="danger"
                                        className='mt-1'
                                        onClick={() => deleteNdaRequest(request.signatureID)}
                                        disabled={isDeleting || isRefreshing || isReminding || isRetrying}
                                    >
                                        <FontAwesomeIcon icon={faTrashAlt} />
                                    </ConfirmButton>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </>
    );
};

export default ContactsNDA2;
